/**
 * Content preview: the exact page model the public site will render (same
 * presentation code: language fallback, formatting, ordering, Markdown, media
 * selection, internal fields removed). Not the public site's visual design,
 * which is deliberately not shared with the admin.
 */
import { useEffect, useMemo, useState } from "react";
import type { WorkUpdate } from "@yushi-portfolio/api-types";
import { CURRENT_SCHEMA_VERSION, type ExhibitionDocument, type WorkDocument } from "@yushi-portfolio/content-schema";
import { validateContentSet } from "@yushi-portfolio/domain";
import { createPresentationContext, createWorkPageModel, type Locale, type MediaModel } from "@yushi-portfolio/presentation";
import type { ApiClient } from "../lib/api";
import { useAssets } from "../lib/assets";

export function WorkPreview({ api, body, id }: { api: ApiClient; body: WorkUpdate; id?: string }) {
  const { assets, mediaBaseUrl } = useAssets();
  const [locale, setLocale] = useState<Locale>("en");
  const [exhibitions, setExhibitions] = useState<ExhibitionDocument[]>([]);

  const exhibitionIds = [...new Set(body.installations.flatMap((i) => (i.exhibitionId ? [i.exhibitionId] : [])))].sort().join(",");
  useEffect(() => {
    const ids = exhibitionIds ? exhibitionIds.split(",") : [];
    Promise.all(ids.map((x) => api.getExhibition(x).then((r) => r.published ?? r.draft, () => undefined))).then((docs) =>
      setExhibitions(docs.filter((d): d is ExhibitionDocument => Boolean(d))),
    );
  }, [exhibitionIds]);

  const { model, problems } = useMemo(() => {
    const timestamp = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
    const work: WorkDocument = { ...body, schemaVersion: CURRENT_SCHEMA_VERSION, id: id ?? "preview", createdAt: timestamp, updatedAt: timestamp };
    const content = { works: [work], exhibitions, assets: assets ?? [] };
    const ctx = createPresentationContext(content, { locale, mediaBaseUrl });
    return { model: createWorkPageModel(work, ctx), problems: validateContentSet(content) };
  }, [body, exhibitions, assets, locale, mediaBaseUrl]);

  return (
    <div className="stack">
      <div className="row-head">
        <p className="muted">公開ページに出る内容と順序の確認用です。見た目は公開サイトのデザインとは異なります。</p>
        <div className="segmented">
          {(["en", "ja"] as const).map((l) => (
            <button key={l} type="button" className={locale === l ? "active" : ""} onClick={() => setLocale(l)}>
              {l === "en" ? "English" : "日本語"}
            </button>
          ))}
        </div>
      </div>
      {problems.length > 0 && (
        <div className="notice warn">
          <strong>このままでは公開できません</strong>
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      <article className="preview">
        <p className="mono">{model.path}</p>
        {model.primaryMedia && <PreviewMedia media={model.primaryMedia} />}
        <h2>{model.title}</h2>
        <p className="muted">
          {model.yearLabel} · {model.typeLabel}
        </p>
        {model.metadata.length > 0 && (
          <dl className="preview-meta">
            {model.metadata.map((m) => (
              <div key={m.label}>
                <dt>{m.label}</dt>
                <dd>{m.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {model.conceptHtml ? <div className="markdown-preview" dangerouslySetInnerHTML={{ __html: model.conceptHtml }} /> : <p className="muted">(Concept なし)</p>}
        {model.media.map((m, i) => (
          <PreviewMedia key={i} media={m} />
        ))}
        {model.installations.map((ins, i) => (
          <section key={i} className="preview-installation">
            <h3>{[ins.title, ins.venue, ins.yearLabel, ins.dimensions].filter(Boolean).join(" / ") || "Installation"}</h3>
            {ins.descriptionHtml && <div dangerouslySetInnerHTML={{ __html: ins.descriptionHtml }} />}
            {ins.media.map((m, j) => (
              <PreviewMedia key={j} media={m} />
            ))}
          </section>
        ))}
        {model.credits.length > 0 && (
          <dl className="preview-meta">
            {model.credits.map((c, i) => (
              <div key={i}>
                <dt>{c.role}</dt>
                <dd>{c.name}</dd>
              </div>
            ))}
          </dl>
        )}
      </article>
    </div>
  );
}

function PreviewMedia({ media }: { media: MediaModel }) {
  return (
    <figure className="preview-media">
      {media.type === "image" ? (
        <img src={media.src} srcSet={media.srcset} sizes="(min-width: 900px) 800px, 100vw" alt={media.alt} width={media.width} height={media.height} />
      ) : (
        <video controls playsInline preload="metadata" poster={media.poster} aria-label={media.label}>
          {media.sources.map((s) => (
            <source key={s.src} src={s.src} type={s.type} />
          ))}
        </video>
      )}
      <figcaption>
        <span className="muted">alt: {media.type === "image" ? media.alt : media.label}</span>
        {media.caption && <span>{media.caption}</span>}
        {media.credit && <span className="muted">{media.credit}</span>}
      </figcaption>
    </figure>
  );
}
